import { Component, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonService } from '@app/services/common.service';
import { NotificationService } from '@app/services/notification.service';
import { ActorType } from '@shared/Constants/commonConstants';

import { AddCouponComponent } from './add-coupon/add-coupon.component';
import { Coupon, CouponType, CouponUsage } from './managed-coupon.model';
import { ManagedCouponService } from './managed-coupon.service';

@Component({
  selector: 'app-managed-coupon',
  templateUrl: './managed-coupon.component.html',
  styleUrls: ['./managed-coupon.component.scss'],
})
export class ManagedCouponComponent implements OnInit {
  formGroup: FormGroup;
  actorType = ActorType;
  couponType = CouponType;
  usage = CouponUsage;

  @ViewChild(MatSort, { static: false }) set matSort(ms: MatSort) {
    if (ms) {
      this.dataSource.sort = ms;
      ms.sortChange.subscribe((sort) => {
        this.sortColumn = sort.active;
        this.sortDirection = sort.direction || 'asc';
        this.updateQueryParams();
      });
    }
  }

  @ViewChild(MatPaginator, { static: false }) set matPaginator(
    mp: MatPaginator
  ) {
    if (mp) {
      this.dataSource.paginator = mp;
    }
  }
  searchValue: string = '';
  usageFilter: number[] = [];
  sortColumn: string = 'createdAt';
  sortDirection: string = 'desc';
  usageFilterOptions = [
    { value: CouponUsage.Pharmacy, label: 'Pharmacy' },
    { value: CouponUsage.Appointment, label: 'Appointment' },
    { value: CouponUsage.Service, label: 'Service' },
    { value: CouponUsage.Packages, label: 'Packages' },
    { value: CouponUsage.StoreBill, label: 'Store Bill' },
  ];

  constructor(
    private managedCouponService: ManagedCouponService,
    private dialog: MatDialog,
    private router: Router,
    private route: ActivatedRoute,
    private notificationService: NotificationService,
    private commonService: CommonService,
    private fb: FormBuilder
  ) {}

  ngOnInit(): void {
    this.formGroup = this.fb.group({
      usage: [[]],
    });
    this.setupFilterPredicate();
    this.loadQueryParams();
    this.loadData();
  }

  private setupFilterPredicate(): void {
    this.dataSource.filterPredicate = (data: Coupon, filter: string) => {
      if (!filter) {
        return true;
      }

      const { search, usage } = JSON.parse(filter) as {
        search: string;
        usage: number[];
      };

      const matchesSearch =
        !search || JSON.stringify(data).toLowerCase().includes(search);
      const matchesUsage =
        !usage?.length ||
        (Array.isArray(data.usage) &&
          usage.some((selected) => data.usage!.includes(Number(selected))));

      return matchesSearch && matchesUsage;
    };
  }

  loadQueryParams() {
    this.route.queryParams.subscribe((params) => {
      this.searchValue = params['search'] || '';

      if (params['usage']) {
        const usageValues = Array.isArray(params['usage'])
          ? params['usage']
          : String(params['usage']).split(',');
        this.usageFilter = usageValues
          .map((value: string) => Number(value))
          .filter((value: number) => !isNaN(value));
      } else {
        this.usageFilter = [];
      }

      this.formGroup.patchValue(
        { usage: this.usageFilter },
        { emitEvent: false }
      );

      if (params['sortColumn']) {
        this.sortColumn = params['sortColumn'];
      } else {
        this.sortColumn = 'createdAt';
      }

      if (params['sortDirection']) {
        this.sortDirection = params['sortDirection'];
      } else {
        this.sortDirection = 'desc';
      }

      this.applyTableFilters();

      if (this.dataSource.sort) {
        this.dataSource.sort.active = this.sortColumn;
        this.dataSource.sort.direction = this.sortDirection as 'asc' | 'desc';
        this.dataSource.sort.sortChange.emit({
          active: this.sortColumn,
          direction: this.sortDirection as 'asc' | 'desc',
        });
      }
    });
  }

  updateQueryParams() {
    const queryParams: any = {};
    if (this.searchValue) {
      queryParams.search = this.searchValue;
    }
    if (this.usageFilter.length) {
      queryParams.usage = this.usageFilter.join(',');
    }
    if (this.sortColumn !== 'createdAt') {
      queryParams.sortColumn = this.sortColumn;
    }
    if (this.sortDirection !== 'desc') {
      queryParams.sortDirection = this.sortDirection;
    }
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: queryParams,
    });
  }

  private applyTableFilters(): void {
    const hasSearch = !!this.searchValue.trim();
    const hasUsage = this.usageFilter.length > 0;

    if (!hasSearch && !hasUsage) {
      this.dataSource.filter = '';
      return;
    }

    this.dataSource.filter = JSON.stringify({
      search: this.searchValue.trim().toLowerCase(),
      usage: this.usageFilter,
    });
  }

  applyFilter(): void {
    this.usageFilter = this.formGroup.get('usage')?.value || [];
    this.applyTableFilters();
    this.updateQueryParams();
  }

  clearFilter(): void {
    this.formGroup.patchValue({ usage: [] });
    this.usageFilter = [];
    this.applyTableFilters();
    this.updateQueryParams();
  }

  private loadData() {
    this.managedCouponService.getCouponList().subscribe((response) => {
      this.dataSource.data = response.coupons as Coupon[];
      this.applyTableFilters();
    });
  }

  add() {
    const dialogRef = this.dialog.open(AddCouponComponent);
    dialogRef.afterClosed().subscribe((isSucces) => {
      if (isSucces) {
        this.notificationService.openSnackBar('Coupon added successfully');
        this.loadData();
      }
    });
  }

  onFilterValueChange(filterValue: string) {
    this.searchValue = filterValue;
    this.applyTableFilters();
    this.updateQueryParams();
  }

  edit(details: Coupon, id: string) {
    const dialogRef = this.dialog.open(AddCouponComponent, {
      data: { ...details, id },
    });
    dialogRef.afterClosed().subscribe((isSuccess) => {
      if (isSuccess) {
        this.notificationService.openSnackBar('Coupon updated successfully');
        this.loadData();
      }
    });
  }

  delete(id: string) {
    this.commonService.confirmDeleteDialog({
      dialog: this.dialog,
      title: 'Warning',
      message: 'Do you want to delete this coupon?',
      onConfirm: () => {
        this.managedCouponService.deleteCoupon(id).subscribe({
          next: () => {
            this.notificationService.openSnackBar(
              'Coupon deleted successfully'
            );
            this.loadData();
          },
          error: (err: { error: { code: string } }) => {
            this.commonService.FailedAlertDialog({
              dialog: this.dialog,
              message: `Errors.${err.error.code}`,
            });
          },
        });
      },
    });
  }

  public getRole(roleType: number) {
    switch (roleType) {
      case ActorType.Admin:
        return 'Admin';
      case ActorType.ClinicManager:
        return 'Hospital Manager';
      case ActorType.OperationManager:
        return 'Operation Manager';
      default:
        return '';
    }
  }

  getUsageNames(usages?: number[]): string {
    if (!usages || usages.length === 0) {
      return 'Not Applicable';
    }

    const usageNames = usages.map((usage) => {
      switch (usage) {
        case CouponUsage.Pharmacy:
          return 'Pharmacy';
        case CouponUsage.Appointment:
          return 'Appointment';
        case CouponUsage.Service:
          return 'Service';
        case CouponUsage.Packages:
          return 'Packages';
        case CouponUsage.StoreBill:
          return 'Store Bill';
        default:
          return 'Unknown';
      }
    });
    return usageNames.join(', ');
  }

  displayedColumns: string[] = [
    'name',
    'value',
    'couponType',
    'maxDiscount',
    'description',
    'creator',
    'expiredAt',
    'maximumUsers',
    'usedCount',
    'applicableIN',
    'action',
  ];
  dataSource: MatTableDataSource<Coupon> = new MatTableDataSource<Coupon>([]);
}
